// =============================================================================
// features/tasks/api.ts — Funções de acesso à API de tarefas.
//
// Todas as chamadas passam por lib/api.ts (único ponto de rede).
// Tipos derivados de @elemento/shared-schemas/tasks.ts — fonte única do
// contrato (F32-S02). Convenção: camelCase, paginação limit/offset — os
// mesmos nomes que a rota `/api/tasks` aceita e devolve.
// =============================================================================

import type { Task, TaskListResponse, TaskStatus, TaskType } from '@elemento/shared-schemas';

import { api } from '../../lib/api';

// ---------------------------------------------------------------------------
// Tipos de query
// ---------------------------------------------------------------------------

export interface TasksQueryParams {
  status?: TaskStatus;
  type?: TaskType;
  /** Filtra tarefas assumidas (claimedBy IS NOT NULL) ou não. Omitir = ambas. */
  claimed?: boolean;
  /** Itens por página (máx 100 — validado pela API). */
  limit?: number;
  /** Deslocamento a partir do primeiro item. */
  offset?: number;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * GET /api/tasks — lista paginada de tarefas.
 * Aplica filtros via query string — apenas parâmetros que a API aceita
 * (status, type, claimed, limit, offset). Não existe filtro por cityId: o
 * escopo de cidade é resolvido no backend a partir do JWT do usuário.
 */
export async function fetchTasks(params: TasksQueryParams = {}): Promise<TaskListResponse> {
  const qs = new URLSearchParams();
  if (params.status !== undefined) qs.set('status', params.status);
  if (params.type !== undefined) qs.set('type', params.type);
  if (params.claimed !== undefined) qs.set('claimed', params.claimed ? 'true' : 'false');
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));

  const query = qs.toString();
  return api.get<TaskListResponse>(`/api/tasks${query ? `?${query}` : ''}`);
}

/**
 * POST /api/tasks/:id/claim — agente assume a tarefa.
 * A tarefa permanece visível após assumida — apenas claimedBy/claimedAt são
 * atualizados; o status permanece 'open' (sem status fantasma 'in_progress').
 */
export async function claimTask(id: string): Promise<Task> {
  return api.post<Task>(`/api/tasks/${encodeURIComponent(id)}/claim`, {});
}

/**
 * POST /api/tasks/:id/complete — marca tarefa como concluída.
 * Após isso a tarefa some da lista filtrada por status 'open'.
 */
export async function completeTask(id: string): Promise<Task> {
  return api.post<Task>(`/api/tasks/${encodeURIComponent(id)}/complete`, {});
}

/**
 * POST /api/tasks/:id/cancel — cancela a tarefa.
 * Após isso a tarefa some da lista filtrada por status 'open'.
 */
export async function cancelTask(id: string): Promise<Task> {
  return api.post<Task>(`/api/tasks/${encodeURIComponent(id)}/cancel`, {});
}
